import { Box } from '@mui/material'
import { EmptyContent } from 'components/common/EmptyContent'
import { FC } from 'react'
import { GRAY_800 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { Path } from 'constants/router'
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined'
import Stack from '@mui/material/Stack'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props
 */
interface Props {
  /** Assignment ID from URL params */
  assignmentId?: string
  /** Property ID from URL params (if available) */
  propertyId?: string
}

/**
 * @component
 * PhotoStudioEmptyContent component displays a placeholder screen when photo studio is accessed without proper state.
 *
 * @example
 * <PhotoStudioEmptyContent assignmentId="123" propertyId="456" />
 */
export const PhotoStudioEmptyContent: FC<Props> = ({ assignmentId, propertyId }) => {
  const { t } = useTranslation(['photo_studio_editing'], { keyPrefix: 'empty_state' })
  const navigate = useNavigate()

  const handleGoToGallery = () => {
    if (propertyId && assignmentId) {
      // Navigate to property gallery with assignment
      navigate(
        Path.PROPERTY_VISUALS_GALLERY_ASSIGNMENT
          .replace(':id', propertyId)
          .replace(':assignmentId', assignmentId)
      )
    } else if (assignmentId) {
      // Navigate to gallery
      navigate(Path.GALLERY.replace(':id', assignmentId))
    } else {
      // Fallback to properties dashboard if no assignment ID
      navigate(Path.INDEX)
    }
  }

  const handleGoToPropertyDashboard = () => {
    navigate(Path.INDEX)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <Box maxWidth="80rem" width="60rem" margin="auto">
        <EmptyContent
          title={t('title')}
          description={t('description')}
          icon={<PhotoOutlinedIcon sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
          button={
            <Stack flexDirection="row" gap=".8rem" alignItems="center" justifyContent="center" width="100%" marginTop="2.4rem">
              <MUIButton
                type="secondaryBorder"
                onClick={handleGoToPropertyDashboard}
              >
                {t('go_to_property_dashboard')}
              </MUIButton>

              <MUIButton
                onClick={handleGoToGallery}
              >
                {t('go_to_gallery')}
              </MUIButton>
            </Stack>
          }
        />
      </Box>
    </Box>
  )
}
